import type {
  HapticImpactStyle,
  HapticNotificationType,
  Platform,
  PlatformUser,
  PlatformViewport,
  SharePreparedResult,
  Unsubscribe,
} from '../types'

import { buildShareUrl } from './shareLink'
import type { TgWebApp, TgWebAppEventPayload, TgWebAppEventType } from './webAppTypes'

/** Причины отказа, которые придумываем сами: Telegram их не присылает. */
const SHARE_ERROR_UNSUPPORTED = 'UNSUPPORTED'
const SHARE_ERROR_UNKNOWN = 'UNKNOWN_ERROR'

const EMPTY_INSETS = { top: 0, right: 0, bottom: 0, left: 0 }

/** `WebApp.shareMessage` — Bot API 8.0, `WebApp.shareToStory` — 7.8. */
const SHARE_MESSAGE_MIN_VERSION = '8.0'
const SHARE_STORY_MIN_VERSION = '7.8'
/**
 * Произвольный `#RRGGBB` в `setHeaderColor` — Bot API 6.9; до неё метод принимал только
 * ключи `bg_color`/`secondary_bg_color` и на цвет отвечал `WebAppHeaderColorKeyInvalid`.
 * Исключение прилетело бы в общий эффект инициализации и оборвало бы всё, что стоит после
 * покраски, — в первую очередь `hideSettingsButton`.
 */
const HEADER_COLOR_MIN_VERSION = '6.9'
/** `requestWriteAccess` — Bot API 6.9. На клиентах старее спросить нечем. */
const WRITE_ACCESS_MIN_VERSION = '6.9'
/** `disableVerticalSwipes` — 7.7, `setBottomBarColor` — 7.10, события safe area — 8.0. */
const VERTICAL_SWIPES_MIN_VERSION = '7.7'
const BOTTOM_BAR_COLOR_MIN_VERSION = '7.10'
const SAFE_AREA_EVENTS_MIN_VERSION = '8.0'

/**
 * События, по которым может поменяться размер окна мини-аппы.
 * `safeAreaChanged` / `contentSafeAreaChanged` есть только с 8.0 — на старых клиентах
 * `onEvent` на неизвестное имя просто ничего не делает, но подписку всё равно не вешаем.
 */
const VIEWPORT_EVENTS: TgWebAppEventType[] = ['viewportChanged']
const SAFE_AREA_EVENTS: TgWebAppEventType[] = ['safeAreaChanged', 'contentSafeAreaChanged']

/**
 * Поддерживает ли клиент версию Bot API. Штатный `isVersionAtLeast` есть не во всех сборках
 * telegram-web-app.js, поэтому при его отсутствии сравниваем `webApp.version` сами —
 * покомпонентно, потому что «7.10» строкой меньше «7.8», а по версии больше.
 */
function isVersionAtLeast(webApp: TgWebApp, required: string): boolean {
  if (typeof webApp.isVersionAtLeast === 'function') {
    return webApp.isVersionAtLeast(required)
  }

  const current = webApp.version.split('.').map(Number)
  const target = required.split('.').map(Number)

  for (let index = 0; index < Math.max(current.length, target.length); index += 1) {
    const currentPart = current[index] ?? 0
    const targetPart = target[index] ?? 0
    if (currentPart !== targetPart) return currentPart > targetPart
  }

  return true
}

function mapUser(webApp: TgWebApp): PlatformUser | null {
  const user = webApp.initDataUnsafe.user
  if (!user) {
    return null
  }

  return {
    id: user.id,
    firstName: user.first_name,
    lastName: user.last_name,
    username: user.username,
    photoUrl: user.photo_url,
    languageCode: user.language_code,
    // Поле необязательное и присылается, только когда разрешение есть: `undefined` — это
    // «разрешения нет» (или клиент старее 6.9), а не «неизвестно».
    allowsWriteToPm: user.allows_write_to_pm === true,
  }
}

/**
 * `viewportStableHeight` — высота окна в последнем устойчивом состоянии, то есть без
 * клавиатуры и без анимации разворачивания. Именно она нам и нужна: клавиатуру мы меряем
 * не здесь, а по `visualViewport` (на iOS `viewportHeight` её не замечает — см. docs/telegram.md §5).
 */
function mapViewport(webApp: TgWebApp): PlatformViewport {
  return {
    safeArea: webApp.safeAreaInset ?? EMPTY_INSETS,
    contentSafeArea: webApp.contentSafeAreaInset ?? EMPTY_INSETS,
    height: webApp.viewportStableHeight || webApp.viewportHeight,
    isExpanded: webApp.isExpanded,
  }
}

/** Адаптер платформы поверх window.Telegram.WebApp. */
export function createTelegramPlatform(webApp: TgWebApp): Platform {
  webApp.ready()

  return {
    kind: 'TELEGRAM',
    initData: webApp.initData || null,
    user: mapUser(webApp),

    /**
     * Геттер, а не снимок: Telegram кладёт `start_param` в `initDataUnsafe` при запуске, но
     * «запуск» — не обязательно новая загрузка вебвью. Если приложение уже было открыто и
     * клиент просто вывел его на передний план по диплинку, объект обновится на месте, а
     * снятое при монтировании значение так и останется прежним — и приглашение потеряется.
     * Читаем каждый раз; когда перечитывать, решает `useStartParam` (см. hooks.ts).
     */
    get startParam(): string | null {
      return webApp.initDataUnsafe.start_param ?? null
    },

    // Геттер даёт всегда свежие значения — подписчики зовут его из onViewportChanged.
    get viewport(): PlatformViewport {
      return mapViewport(webApp)
    },

    onViewportChanged(handler: () => void): Unsubscribe {
      const events = isVersionAtLeast(webApp, SAFE_AREA_EVENTS_MIN_VERSION)
        ? [...VIEWPORT_EVENTS, ...SAFE_AREA_EVENTS]
        : VIEWPORT_EVENTS

      for (const event of events) webApp.onEvent(event, handler)
      return () => {
        for (const event of events) webApp.offEvent(event, handler)
      }
    },

    backButton: {
      show() {
        webApp.BackButton.show()
      },
      hide() {
        webApp.BackButton.hide()
      },
      onClick(handler: () => void): Unsubscribe {
        webApp.BackButton.onClick(handler)
        return () => webApp.BackButton.offClick(handler)
      },
    },

    haptic: {
      impact(style: HapticImpactStyle) {
        webApp.HapticFeedback.impactOccurred(style)
      },
      notification(type: HapticNotificationType) {
        webApp.HapticFeedback.notificationOccurred(type)
      },
      selection() {
        webApp.HapticFeedback.selectionChanged()
      },
    },

    storage: {
      get(key: string) {
        return new Promise((resolve) => {
          if (!webApp.CloudStorage) {
            resolve(window.localStorage.getItem(key))
            return
          }
          webApp.CloudStorage.getItem(key, (error, value) => {
            resolve(error ? window.localStorage.getItem(key) : (value ?? null))
          })
        })
      },
      set(key: string, value: string) {
        return new Promise((resolve) => {
          window.localStorage.setItem(key, value)
          if (!webApp.CloudStorage) {
            resolve()
            return
          }
          webApp.CloudStorage.setItem(key, value, () => resolve())
        })
      },
      remove(key: string) {
        return new Promise((resolve) => {
          window.localStorage.removeItem(key)
          if (!webApp.CloudStorage) {
            resolve()
            return
          }
          webApp.CloudStorage.removeItem(key, () => resolve())
        })
      },
    },

    share: {
      openTelegramLink(url: string) {
        webApp.openTelegramLink(url)
      },

      openLink(url: string) {
        // Без `try_instant_view`: Instant View собирается по разметке статьи и на юридическом
        // документе даёт обрезанный пересказ вместо самой страницы.
        webApp.openLink(url)
      },

      shareToChat({ text, url }) {
        const target = url ?? ''
        webApp.switchInlineQuery(`${text}${target ? ` ${target}` : ''}`.trim())
      },

      shareUrl({ text, url }) {
        // Сборка адреса — в shareLink.ts: там же объяснено, почему не URLSearchParams.
        webApp.openTelegramLink(buildShareUrl({ text, url }))
      },

      sharePreparedMessage(preparedMessageId: string) {
        // Проверка версии — здесь, а не в компонентах (правило 6): наружу уходит только ответ
        // «получилось или нет», по которому вызывающая сторона выбирает следующий способ.
        if (!isVersionAtLeast(webApp, SHARE_MESSAGE_MIN_VERSION) || !webApp.shareMessage) {
          return Promise.resolve({ isSent: false, error: SHARE_ERROR_UNSUPPORTED })
        }

        return new Promise<SharePreparedResult>((resolve) => {
          // В колбэк `shareMessage` приходит голый `false`, а причина — отдельным событием.
          // Подписываемся до вызова: обработчики Telegram зовёт в порядке подписки, и наш
          // должен успеть записать причину раньше, чем сработает колбэк.
          let error: string | undefined
          const handleFailed = (payload: TgWebAppEventPayload) => {
            error = payload?.error || SHARE_ERROR_UNKNOWN
          }
          webApp.onEvent('shareMessageFailed', handleFailed)

          const finish = (isSent: boolean) => {
            webApp.offEvent('shareMessageFailed', handleFailed)
            resolve(isSent ? { isSent } : { isSent, error: error ?? SHARE_ERROR_UNKNOWN })
          }

          try {
            webApp.shareMessage?.(preparedMessageId, (isSent) => finish(Boolean(isSent)))
          } catch {
            // Клиент заявил поддержку, но метод бросил — уходим в фолбэк, а не падаем.
            finish(false)
          }
        })
      },

      shareToStory(mediaUrl: string, params) {
        if (!isVersionAtLeast(webApp, SHARE_STORY_MIN_VERSION) || !webApp.shareToStory) {
          return false
        }

        webApp.shareToStory(mediaUrl, {
          text: params?.text,
          widget_link: params?.widgetLink,
        })

        return true
      },
    },

    requestWriteAccess() {
      // Проверка версии — здесь, как и у шэринга (правило 6): наружу уходит только
      // «разрешили или нет», и вызывающая сторона на версии клиента не завязывается.
      if (!isVersionAtLeast(webApp, WRITE_ACCESS_MIN_VERSION) || !webApp.requestWriteAccess) {
        return Promise.resolve(false)
      }

      return new Promise<boolean>((resolve) => {
        // Колбэк зовётся при закрытии попапа — и на «Разрешить», и на «Отклонить».
        // Свайпом попап закрыть можно, а колбэк тогда может и не прийти: висящий промис
        // никого не блокирует (мы его не ждём), но и «разрешили» из него не выйдет.
        try {
          webApp.requestWriteAccess?.((isGranted) => resolve(Boolean(isGranted)))
        } catch {
          // Клиент заявил версию, но метода в его сборке telegram-web-app.js нет.
          resolve(false)
        }
      })
    },

    close() {
      webApp.close()
    },
    expand() {
      webApp.expand()
    },
    setHeaderColor(color: string) {
      if (!isVersionAtLeast(webApp, HEADER_COLOR_MIN_VERSION)) return
      webApp.setHeaderColor(color)
    },
    setBackgroundColor(color: string) {
      webApp.setBackgroundColor(color)
    },
    setBottomBarColor(color: string) {
      if (!isVersionAtLeast(webApp, BOTTOM_BAR_COLOR_MIN_VERSION)) return
      webApp.setBottomBarColor?.(color)
    },

    hideSettingsButton() {
      // На клиентах до 6.10 объекта нет вовсе, и пункта в меню тоже нет — молча выходим.
      webApp.SettingsButton?.hide()
    },

    setVerticalSwipesEnabled(isEnabled: boolean) {
      if (!isVersionAtLeast(webApp, VERTICAL_SWIPES_MIN_VERSION)) return

      // Метод может отсутствовать, даже когда версия подходит: сборки telegram-web-app.js
      // расходятся с версией клиента. Опциональный вызов вместо падения всего приложения.
      if (isEnabled) webApp.enableVerticalSwipes?.()
      else webApp.disableVerticalSwipes?.()
    },
  }
}
